import { format } from 'date-fns'
import { data, useLoaderData } from 'react-router'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/accordion.tsx'
import {
	Page,
	PageTopBarBackButtonLink,
	PageTopBarHeading,
} from '#app/components/pageSetup/page'
import { Badge } from '#app/components/ui/badge.tsx'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card'
import { prisma } from '#app/utils/db.server'
import { type Route } from './+types/updates'

export async function loader() {
	const appUpdates = await prisma.appUpdate.findMany({
		where: {
			isPublished: true,
		},
		orderBy: {
			releaseDate: 'desc',
		},
	})

	return data({ appUpdates })
}

export default function UpdatesRoute() {
	const { appUpdates } = useLoaderData<typeof loader>()

	return (
		<Page
			topBar={{
				heading: <PageTopBarHeading icon="update" heading="App Updates" />,
				actions: (
					<PageTopBarBackButtonLink to="/" tooltipLabel="Back to Home" />
				),
			}}
		>
			<div className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle>What's New in InSelect</CardTitle>
						<CardDescription>
							Stay up to date with the latest features, improvements, and bug
							fixes.
						</CardDescription>
					</CardHeader>
					<CardContent>
						{appUpdates.length === 0 ? (
							<p className="py-8 text-center text-gray-500">
								No updates available at this time.
							</p>
						) : (
							<Accordion type="single" collapsible className="w-full">
								{appUpdates.map((update) => (
									<AccordionItem key={update.id} value={update.id}>
										<AccordionTrigger className="group hover:no-underline">
											<div className="flex items-center gap-2 text-left">
												<span className="font-medium">{update.title}</span>
												<span className="text-xs text-gray-500">
													(v{update.version})
												</span>
												{update.isCritical && (
													<Badge className="ml-2">Critical</Badge>
												)}
												<span className="ml-auto text-xs text-gray-500">
													{format(new Date(update.releaseDate), 'MMM d, yyyy')}
												</span>
											</div>
										</AccordionTrigger>
										<AccordionContent>
											<div className="space-y-4 pt-2">
												<p className="text-gray-700">{update.description}</p>

												{update.changelog && (
													<div className="mt-4">
														<h4 className="mb-2 text-sm font-medium">
															Changelog:
														</h4>
														<div className="rounded-md bg-gray-50 p-4">
															<pre className="whitespace-pre-wrap text-sm text-gray-700">
																{update.changelog}
															</pre>
														</div>
													</div>
												)}
											</div>
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>
						)}
					</CardContent>
				</Card>
			</div>
		</Page>
	)
}

export const meta: Route.MetaFunction = () => {
	return [
		{ title: 'InSelect | App Updates' },
		{
			name: 'description',
			content:
				'See the latest updates, features, and improvements to InSelect.',
		},
	]
}
